/** The groups the measurements of a vehicle are sorted into. */
export type MotorTheme =
  | "engine"
  | "state"
  | "fuel"
  | "electrical"
  | "temperatures"
  | "distance"
  | "telematics"
  | "other";

/** Display order, chosen so the engine and the vehicle state come before the telematics unit. */
export const THEME_ORDER: MotorTheme[] = [
  "engine",
  "state",
  "fuel",
  "electrical",
  "temperatures",
  "distance",
  "telematics",
  "other",
];

/** What is known about a diagnostic, as far as naming it goes. */
export interface DiagnosticNaming {
  name?: string;
  source?: { id?: string; name?: string };
}

/**
 * Geotab's own diagnostic ids read like `DiagnosticEngineSpeedId`, and matching words inside one
 * is sound. Ids a customer or a third party added are opaque strings, and a word found inside one
 * is a coincidence — the same trap that had an engine coolant temperature under the id `ad8Qw1`
 * counting as a tachograph reading on the live data page.
 */
const KNOWN_ID = /^[A-Z][A-Za-z]*Id$/;

/** First match wins, so the more specific groups come first. */
const RULES: { theme: MotorTheme; pattern: RegExp }[] = [
  { theme: "engine", pattern: /\b(engine|rpm|coolant|gear|torque|crank)/ },
  { theme: "state", pattern: /\b(ignition|active|seatbelt|door|parking|motion)/ },
  { theme: "fuel", pattern: /\b(fuel|diesel|adblue|exhaust ?fluid|consumption)/ },
  { theme: "electrical", pattern: /\b(voltage|power|battery|current|alternator)/ },
  { theme: "temperatures", pattern: /\b(temp|temperature)/ },
  { theme: "distance", pattern: /\b(odometer|distance|trip|mileage)/ },
  {
    theme: "telematics",
    pattern: /\b(iox|thirdparty|third ?party|signal|acceler|gyro|gps|satellite|cell|lte|rssi|modem|go ?device)/,
  },
];

/**
 * Works out which group a measurement belongs in.
 *
 * @param diagnosticId - The diagnostic id.
 * @param diagnostic - What is known about the diagnostic, if anything.
 * @returns The group, `"other"` when nothing matched.
 */
export function inferTheme(diagnosticId: string, diagnostic?: DiagnosticNaming): MotorTheme {
  const haystack = buildHaystack(diagnosticId, diagnostic);

  for (const rule of RULES) {
    if (rule.pattern.test(haystack)) {
      return rule.theme;
    }
  }

  return "other";
}

/**
 * The text the rules are matched against: the diagnostic name and its source always, and the id
 * only when it is one of Geotab's own.
 *
 * Geotab's ids are also split on their word boundaries — `DiagnosticEngineSpeedId` has no spaces
 * in it, so `\bengine` would never match it otherwise.
 */
function buildHaystack(diagnosticId: string, diagnostic?: DiagnosticNaming): string {
  const parts = [diagnostic?.name ?? "", diagnostic?.source?.name ?? ""];

  // Source ids follow the same convention as diagnostic ids -- SourceThirdPartyId, SourceObdId --
  // so they get the same treatment: split on word boundaries, and only when they look like one.
  for (const id of [diagnosticId, diagnostic?.source?.id ?? ""]) {
    if (KNOWN_ID.test(id)) {
      parts.push(spaceOutWords(id));
    }
  }

  return parts.join(" ").toLowerCase();
}

function spaceOutWords(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}
